import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Search, RefreshCw, Calendar, TrendingUp, TrendingDown } from 'lucide-react';

const AllTransactionsPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  const apiUrl = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      // Try to fetch from backend, fallback to default data
      const response = await fetch(`${apiUrl}/api/excel/get-transactions`);
      const data = await response.json();
      
      if (data.status === 'success' && data.transactions && data.transactions.length > 0) {
        setTransactions(data.transactions);
      } else {
        // Use default transactions if no backend data
        setTransactions(defaultTransactions);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions(defaultTransactions);
    } finally {
      setLoading(false);
    }
  };

  // Default transactions data
  const defaultTransactions = [
    { id: 1, name: 'Martinez Wedding - Final Payment', date: '2025-12-28', client: 'Emily Martinez', amount: 2250.00, type: 'income', category: 'Photography' },
    { id: 2, name: 'Chen Corporate Event Payment', date: '2025-12-27', client: 'Tech Corp', amount: 3800.00, type: 'income', category: 'Events' },
    { id: 3, name: 'Adobe Creative Cloud', date: '2025-12-26', client: 'Adobe Inc', amount: 54.99, type: 'expense', category: 'Software' },
    { id: 4, name: 'Equipment Insurance - Quarterly', date: '2025-12-26', client: 'Photography Insurance Co', amount: 450.00, type: 'expense', category: 'Insurance' },
    { id: 5, name: 'Studio Rent - December', date: '2025-12-01', client: 'Property Management LLC', amount: 1200.00, type: 'expense', category: 'Rent' },
    { id: 6, name: 'Johnson Family Portrait', date: '2025-12-20', client: 'Sarah Johnson', amount: 850.00, type: 'income', category: 'Photography' },
    { id: 7, name: 'Camera Lens Rental', date: '2025-12-18', client: 'LensRentals.com', amount: 125.00, type: 'expense', category: 'Equipment' },
    { id: 8, name: 'Wilson Engagement Shoot', date: '2025-12-15', client: 'Michael Wilson', amount: 500.00, type: 'income', category: 'Photography' },
    { id: 9, name: 'Printing Services - Albums', date: '2025-12-14', client: 'Professional Print Co', amount: 380.00, type: 'expense', category: 'Printing' },
    { id: 10, name: 'Tech Summit Photography', date: '2025-12-10', client: 'Tech Summit Inc', amount: 2500.00, type: 'income', category: 'Events' },
    { id: 11, name: 'Website Hosting - Annual', date: '2025-12-05', client: 'Squarespace', amount: 144.00, type: 'expense', category: 'Software' },
    { id: 12, name: 'Garcia Quinceañera', date: '2025-12-03', client: 'Garcia Family', amount: 1800.00, type: 'income', category: 'Events' },
    { id: 13, name: 'Memory Cards & Storage', date: '2025-12-02', client: 'B&H Photo', amount: 89.99, type: 'expense', category: 'Equipment' },
    { id: 14, name: 'Patel Wedding Deposit', date: '2025-11-28', client: 'Anita Patel', amount: 1500.00, type: 'income', category: 'Photography' },
    { id: 15, name: 'Lighting Equipment', date: '2025-11-25', client: 'Adorama', amount: 299.00, type: 'expense', category: 'Equipment' },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Filter and sort transactions
  const filteredTransactions = transactions
    .filter(txn => {
      const matchesSearch = (txn.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (txn.client || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (txn.category || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = typeFilter === 'all' || txn.type === typeFilter;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'date') {
        aVal = new Date(aVal || '1970-01-01');
        bVal = new Date(bVal || '1970-01-01');
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const totalIncome = filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalExpenses = filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + (t.amount || 0), 0);
  const netAmount = totalIncome - totalExpenses;

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/finances-re')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">All Transactions</h1>
            <p className="text-gray-400 text-sm">View all income and expenses</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={fetchTransactions}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <button className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors">
            <Download size={18} />
            Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp size={20} className="text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Income</p>
              <p className="text-xl font-bold text-green-400">{formatCurrency(totalIncome)}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <TrendingDown size={20} className="text-red-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Expenses</p>
              <p className="text-xl font-bold text-red-400">{formatCurrency(totalExpenses)}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${netAmount >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              {netAmount >= 0 ? <TrendingUp size={20} className="text-green-400" /> : <TrendingDown size={20} className="text-red-400" />}
            </div>
            <div>
              <p className="text-gray-400 text-sm">Net</p>
              <p className={`text-xl font-bold ${netAmount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(netAmount)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'income', 'expense'].map((type) => (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === type
                  ? type === 'income' ? 'bg-green-600 text-white' 
                  : type === 'expense' ? 'bg-red-600 text-white'
                  : 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th 
                  className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center gap-2">
                    Date
                    {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th 
                  className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">
                    Description
                    {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th 
                  className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                  onClick={() => handleSort('client')}
                >
                  <div className="flex items-center gap-2">
                    Client/Vendor
                    {sortField === 'client' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th 
                  className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                  onClick={() => handleSort('category')}
                >
                  <div className="flex items-center gap-2">
                    Category
                    {sortField === 'category' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th className="text-center p-4 text-gray-400 font-medium">Type</th>
                <th 
                  className="text-right p-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center justify-end gap-2">
                    Amount
                    {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500">
                    <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                    Loading transactions...
                  </td>
                </tr>
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500">
                    No transactions found
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((txn, idx) => (
                  <tr 
                    key={txn.id || idx} 
                    className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Calendar size={14} className="text-gray-500" />
                        {formatDate(txn.date)}
                      </div>
                    </td>
                    <td className="p-4 font-medium">{txn.name || '-'}</td>
                    <td className="p-4 text-gray-400">{txn.client || '-'}</td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-gray-800 rounded text-sm text-gray-300">
                        {txn.category || '-'}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        txn.type === 'income' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {txn.type === 'income' ? 'Income' : 'Expense'}
                      </span>
                    </td>
                    <td className={`p-4 text-right font-semibold ${
                      txn.type === 'income' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-800 bg-gray-900/50 flex items-center justify-between">
          <p className="text-gray-400 text-sm">
            Showing {filteredTransactions.length} of {transactions.length} transactions
          </p>
          <p className="text-gray-400 text-sm">
            Net: <span className={`font-semibold ${netAmount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(netAmount)}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AllTransactionsPage;
